import React from 'react';
import { Lightbulb } from 'lucide-react';
import { C } from '../../theme.js';
import { Alert, Eyebrow, FieldLabel, TextInput } from '../../components/Primitives.jsx';
import { Segmented } from '../StepInspector.jsx';
import { MultiColsDropdown } from './MultiColsDropdown.jsx';
import { resolveLiveRows } from './columnClassification.js';
import { hasUpstreamSplit } from '../../data.js';
import { DecimalPlacesField } from '../shared/DecimalPlacesField.jsx';
import { OutputColumnNamesField } from '../shared/OutputColumnNamesField.jsx';

function computeZScorePreview(rows, selCols, zMultiplier) {
  if (!rows || !rows.length || !selCols || !selCols.length) return null;
  const colName = selCols[0];
  if (!colName) return null;

  let trainRows = rows;
  for (const key of Object.keys(rows[0] || {})) {
    const vals = new Set(rows.map(r => r[key]).filter(v => v !== null && v !== ''));
    if (vals.size > 0 && Array.from(vals).every(v => v === 'train' || v === 'test')) {
      const filtered = rows.filter(r => r[key] === 'train');
      if (filtered.length > 0) trainRows = filtered;
      break;
    }
  }

  const nums = trainRows
    .map(r => {
      const v = r[colName];
      if (v === null || v === undefined || v === '') return NaN;
      return typeof v === 'number' ? v : parseFloat(String(v).replace(/[$,]/g, ''));
    })
    .filter(v => !isNaN(v));

  if (nums.length === 0) return null;

  const mean = nums.reduce((a, b) => a + b, 0) / nums.length;
  let variance = 0;
  if (nums.length > 1) {
    variance = nums.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / (nums.length - 1); // Sample variance
  }
  const std = Math.sqrt(variance);

  const lowVal = mean - zMultiplier * std;
  const highVal = mean + zMultiplier * std;

  let affectedCount = 0;
  for (const r of rows) {
    let isAffected = false;
    for (const col of selCols) {
      const val = r[col];
      if (val === null || val === undefined || val === '') continue;
      const num = typeof val === 'number' ? val : parseFloat(String(val).replace(/[$,]/g, ''));
      if (!isNaN(num) && (num < lowVal || num > highVal)) {
        isAffected = true;
        break;
      }
    }
    if (isAffected) affectedCount++;
  }

  const affectedPct = Math.round((affectedCount / rows.length) * 1000) / 10;

  return {
    lowVal: Math.round(lowVal * 1000) / 1000,
    highVal: Math.round(highVal * 1000) / 1000,
    affectedCount,
    affectedPct
  };
}

export function ZScoreOutlierInspector({ step, onChange, pipeline, uploadedRows }) {
  const c = step.config || {};
  const sel = Array.isArray(c.columns) ? c.columns : (c.column ? [c.column] : []);
  const u = (patch) => onChange({ ...step, config: { ...c, ...patch } });
  const splitReady = pipeline ? hasUpstreamSplit(pipeline.steps, step.id) : true;
  const rows = resolveLiveRows(uploadedRows, pipeline);

  const z = c.z ?? 3.0;
  const action = c.action || 'clip';

  const preview = computeZScorePreview(rows, sel, z);

  const applyPresetChip = (newZ) => {
    u({ z: newZ });
  };

  return (
    <div>
      <Eyebrow>Z-Score Method</Eyebrow>
      <div style={{ color: C.fg4, fontSize: '.78rem', margin: '4px 0 10px', lineHeight: 1.5 }}>
        Cap or drop outliers using standard deviations from the mean (Z-Score).
      </div>

      {!splitReady && (
        <Alert kind="warn" style={{ marginBottom: 10 }}>
          Warning: No Train / Test Split step detected upstream. If your end goal is machine learning modeling, please add a Train / Test Split block before performing output handling to avoid data leakage.
        </Alert>
      )}

      <MultiColsDropdown selected={sel} onChange={(v) => u({ columns: v, column: v[0] || null })} numeric />

      <div style={{ marginTop: 10 }}>
        <FieldLabel hint="Cap values outside bounds or drop outlier rows">Action on Outliers</FieldLabel>
        <Segmented
          value={action}
          onChange={(v) => u({ action: v })}
          options={[
            { value: 'clip', label: 'Clip (Cap values)' },
            { value: 'drop', label: 'Drop rows' }
          ]}
        />
      </div>

      <div style={{ marginTop: 12 }}>
        <FieldLabel hint="Quick selection for Z threshold">Presets</FieldLabel>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { label: 'Standard (±2.5σ)', val: 2.5 },
            { label: 'Strict (±3.0σ)', val: 3.0 },
            { label: 'Conservative (±3.5σ)', val: 3.5 },
          ].map(p => {
            const active = z === p.val;
            return (
              <button
                key={p.label}
                type="button"
                onClick={() => applyPresetChip(p.val)}
                style={{
                  background: active ? C.accentStrong : C.input,
                  color: active ? '#fff' : C.fg3,
                  border: `1px solid ${active ? C.accentStrong : C.borderStrong}`,
                  borderRadius: 6, padding: '4px 8px', fontSize: '.74rem',
                  fontWeight: 600, cursor: 'pointer', transition: 'all .15s'
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <FieldLabel hint="Threshold (Z)">Threshold (Z): {z}</FieldLabel>
        <TextInput
          type="number"
          step="0.2"
          min="0"
          value={z}
          onChange={(v) => u({ z: parseFloat(v) || 0 })}
          style={{ width: 100 }}
        />
      </div>

      {preview && (
        <div style={{
          marginTop: 12, padding: '10px 12px',
          background: 'var(--info-bg)', border: `1px solid ${C.accentMuted}`,
          borderRadius: 8, fontSize: '.76rem', color: C.fg1, lineHeight: 1.5
        }}>
          <Lightbulb size={13} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} /> <strong>Preview:</strong> Based on the full dataset, a Z-Score threshold of <strong>{z}</strong> {action === 'drop' ? 'drops' : 'clips'} values below <strong>{preview.lowVal}</strong> and above <strong>{preview.highVal}</strong>. This will affect <strong>{preview.affectedCount}</strong> rows (<strong>{preview.affectedPct}%</strong> of data).
        </div>
      )}

      {action !== 'drop' && (
        <OutputColumnNamesField
          columns={sel}
          value={c.output_columns}
          onChange={(v) => u({ output_columns: v })}
          legacyValue={c.output_column}
          autoName={(col) => `${col}_zscore`}
        />
      )}
      <DecimalPlacesField value={c.output_decimals} onChange={(v) => u({ output_decimals: v })} />
    </div>
  );
}

